import type { FC } from 'react';
import Form from '@components/Form';
import { SelectMenu } from '@components/SelectMenu/SelectMenu';
import { WysiwygEditor } from '@ContractBuilder/components/Wysiwyg';
import type { BlockEditFormState } from '@ContractBuilder/modules/block-edit';
import { useBlockEditFieldProps } from '@ContractBuilder/modules/block-edit/hooks/use-block-edit-field-props';
import { INLINE_BLOCK_EDITOR_NAME } from '@providers/editors-map';
import type { ResourceBlock } from '@root/@types/base';
import type { JSONSchema } from '@root/@types/types';
import { ReactRenderer } from '@tiptap/react';
import { getContractDatapointControls } from '@WysiwygEditor/components/Controls/utils/datapoint-controls';
import { getSelectMenuItemsForContracts } from '@WysiwygEditor/components/Controls/utils/datapoint-options';
import { getCurrentContentId } from '@WysiwygEditor/components/Editor/extensions/helpers';
import type { DatapointInserterRenderFactory } from '@WysiwygEditor/components/Editor/types';
import { get } from 'lodash-es';

type BlockState = Pick<
  ResourceBlock,
  'content' | 'name' | 'section_id' | 'title' | 'type' | 'shouldInsertPageBreak' | 'variations' | 'visibility'
> & {
  id?: string;
  order?: number;
};

interface InlineBlockEditProps {
  className?: string;
  currentVariationIdx: number;
  formValues?: BlockEditFormState;
  hasVariations: boolean;
  isEndorsementView: boolean;
  onSubmit: () => Promise<void>;
  schema: JSONSchema | null;
}

export const InlineBlockEdit: FC<InlineBlockEditProps> = ({
  className,
  currentVariationIdx,
  formValues,
  hasVariations,
  isEndorsementView,
  onSubmit,
  schema,
}) => {
  const { name: contentFieldAccessor, onChange: contentFieldChangeHandler } = useBlockEditFieldProps<BlockState>(
    hasVariations,
    currentVariationIdx,
    'content',
  );
  const { name: titleFieldAccessor, onChange: titleFieldChangeHandler } = useBlockEditFieldProps<BlockState>(
    hasVariations,
    currentVariationIdx,
    'title',
  );

  const datapointInserterItems = getSelectMenuItemsForContracts(schema);
  const getDatapointInserterRenderer: DatapointInserterRenderFactory = (
    props,
    items,
    handleInsertDatapoint,
    handleCancel,
  ) =>
    new ReactRenderer(SelectMenu, {
      props: {
        className: 'max-h-[364px]',
        items,
        range: props.range,
        filterText: props.query,
        expandFirstLevel: false,
        isSearchable: false,
        maxTextWidth: 350,
        formValues,
        onClose: handleCancel,
        onItemClick: handleInsertDatapoint,
        disableEntries: props.disableEntries,
      },
      editor: props.editor,
    });

  const datapointProps = isEndorsementView
    ? {}
    : {
        getDatapointControls: getContractDatapointControls,
        getDatapointInserterRenderer,
        datapointInserterItems,
      };

  return (
    <Form
      className="group/import relative flex w-full flex-col bg-white pb-3 data-[docx-import=open]:h-[218px] data-[docx-import=open]:min-w-[794px] data-[docx-import=open]:overflow-hidden"
      onSubmit={onSubmit}
    >
      <div className="group-data-[docx-import=open]/import:hidden">
        <WysiwygEditor
          blockType={formValues?.type}
          className={className}
          content={get(formValues, contentFieldAccessor, '') as string}
          currentContentId={getCurrentContentId(formValues, currentVariationIdx)}
          handleTitleChange={titleFieldChangeHandler}
          name={INLINE_BLOCK_EDITOR_NAME}
          onUpdate={contentFieldChangeHandler}
          shouldShowControls={false}
          shouldShowHelperText={false}
          title={get(formValues, titleFieldAccessor, '') as string}
          titleFieldName={titleFieldAccessor}
          {...datapointProps}
        />
      </div>
    </Form>
  );
};
